import axios, { AxiosRequestConfig, Method, AxiosError } from 'axios';
import { getUserTimezone } from './dateUtils';
import { deauthenticateUser, getToken, isUserAuthenticated } from './tokenManager';

const getUserLocale = () => {
    const userLocale = navigator.language;
    return userLocale;
};

export const isRejected = (input: PromiseSettledResult<unknown>): input is PromiseRejectedResult =>
    input.status === 'rejected';

export const isFulfilled = <T>(
    input: PromiseSettledResult<T>,
): input is PromiseFulfilledResult<T> => input.status === 'fulfilled';

export type RequestResponse<T> = {
    success: boolean;
    status: number;
    payload: T;
    error?: Error;
    message?: string;
    headers: Record<string, string>;
};

const requestHandler = async <R>(
    method: Method,
    url: string,
    data?: unknown,
    options: AxiosRequestConfig = {},
    usageType?: string,
    defaultResponse = false,
    usageCheck = false,
    featureFlagCheck = false,
): Promise<RequestResponse<R>> => {
    let response;
    const headers = {
        'X-User-Timezone': getUserTimezone(),
        'X-User-Locale': getUserLocale(),
    };
    const requestObj = { method, url, headers, ...options };
    const token = getToken();
    const { user } = isUserAuthenticated();
    if (user && user.id && token) {
        requestObj.headers = {
            ...requestObj.headers,
            usageCheck,
            featureFlagCheck,
            usageType,
            Authorization: `Bearer ${token}`,
        };
    }

    if (data) requestObj.data = data;
    try {
        const res = await axios(requestObj);
        if (res.data && res.data.authError) {
            deauthenticateUser();
            if (token) window.location.reload();
        }
        if (defaultResponse)
            response = {
                ...res,
                success:
                    res.data instanceof ArrayBuffer
                        ? res.headers['x-success-status'] === 'true'
                        : res.data.success,
            };
        else {
            response = {
                ...res.data,
                status: res.status,
                headers: res.headers,
                error: null,
            };
        }
    } catch (e) {
        const error = e as AxiosError;
        if (error.response) {
            response = {
                ...(error.response.data ?? {}),
                status: error.response.status,
                headers: error.response.headers,
                error: 'Server responded with an error',
                success: false,
            };
        } else if (error.request) {
            response = {
                ...error.request,
                error: 'Request Timeout',
                success: false,
            };
        } else {
            response = {
                errMsg: error.message,
                error: 'Client Error: Bad Request',
                success: false,
            };
        }
        if (response.payload && response.payload.authError) {
            deauthenticateUser();
            if (token) window.location.reload();
        }
        // if (!response.status) {
        //     requestHandler('POST', user && user.id && token ? '/api/dataErrorReport' : '/api/public/dataErrorReport', {
        //         error: {
        //             message: response.error,
        //             route: url,
        //             data: response.errMsg,
        //         },
        //         user,
        //         version: process.env.APP_VERSION,
        //         location: `${window.location.pathname}${window.location.hash}`,
        //     });
        // }
    }

    return response;
};

/**
 * API response
 * @typedef {Object} response
 * @property {boolean} success - whether request was successful
 * @property {string} message - message from server
 * @property {Object} payload - data returned from server
 * @property {number} status - status code from server
 * @property {Object} headers - headers from response
 * @property {Object} error - error obj or null if no error
 */

export default requestHandler;
